import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { PharmacIcon } from '@pharmac/ui'
import CSS from 'csstype'
import React, { HTMLAttributes, PropsWithChildren, useEffect, useState } from 'react'
import { useClickOutside } from '../../hooks'
import { MediaQuery, useBreakpoint } from '../../hooks/useBreakpoints'
import { ExtranetContainer } from '../ExtranetContainer'

type ExtranetModalRootProps = { modalBackground?: false | CSS.Properties['background'] }

const backgroundStyle = (p: ExtranetModalRootProps) => {
  if (typeof p.modalBackground === 'boolean' && !p.modalBackground) {
    return `background: none`
  }

  return css`
    background: ${p.modalBackground
      ? p.modalBackground
      : 'linear-gradient(130.84deg, rgba(136, 40, 143, 0.83) 65.47%, #66d0d5 99.95%);'};
  `
}

const ExtranetModalRoot = styled.div<ExtranetModalRootProps>`
  position: fixed;

  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${backgroundStyle}

  z-index: 600;
`

const ExtranetModalContainer = styled.div<{ wideModal?: boolean }>`
  position: relative;
  width: calc(100% - var(--spacing-medium));

  @media (min-width: 800px) {
    width: ${({ wideModal }) =>
      wideModal ? 'calc(1010px - var(--spacing-medium))' : 'calc(800px - var(--spacing-medium))'};
  }

  background-color: var(--color-accent-1);
  border-radius: var(--border-radius-small);
  box-shadow: var(--shadow-large);

  display: flex;
  flex-direction: column;

  padding-top: var(--spacing-large);
  padding-left: var(--spacing-medium);
  padding-right: var(--spacing-medium);
  padding-bottom: var(--spacing-medium);
  overflow-y: auto;

  ${MediaQuery.maxMobile} {
    padding: var(--spacing-medium);
  }
`

const ExtranetModalContainerInner = styled.div``

const ExtranetModalBodyRoot = styled.div`
  padding: var(--spacing-large) 0;

  ::-webkit-scrollbar {
    width: 10px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 10px;
  }
`

const ExtranetModalHeaderRoot = styled.div`
  line-height: var(--typo-h4-line-height);
  font-size: var(--typo-h4-size);
  font-weight: var(--typo-h4-weight);
`

const ExtranetModalFooterRoot = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;

  button:not(:first-of-type) {
    margin-left: var(--spacing-small);
  }
`

const ExtranetModalCloseButton = styled.div`
  position: absolute;
  right: var(--spacing-medium);
  top: var(--spacing-medium);

  width: 24px;
  height: 24px;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    cursor: pointer;

    svg {
      color: gray;
    }
  }
`

export const ExtranetModalBody = (props: PropsWithChildren & HTMLAttributes<HTMLDivElement>) => {
  const { children, ...divProps } = props

  return <ExtranetModalBodyRoot {...divProps}>{children}</ExtranetModalBodyRoot>
}

type ExtranetModalHeaderProps = PropsWithChildren & HTMLAttributes<HTMLDivElement>

export const ExtranetModalHeader = (props: ExtranetModalHeaderProps) => {
  const { children, ...divProps } = props

  return <ExtranetModalHeaderRoot {...divProps}>{children}</ExtranetModalHeaderRoot>
}

export const ExtranetModalFooter = (props: PropsWithChildren & HTMLAttributes<HTMLDivElement>) => {
  const { children, ...divProps } = props

  return <ExtranetModalFooterRoot {...divProps}>{children}</ExtranetModalFooterRoot>
}

type ExtranetModalProps = PropsWithChildren & {
  showOnProp?: boolean | string | null
  closeModal?: () => void
  buttons?: JSX.Element[]
  footerContent?: React.ReactNode
  closeOnOutsideClick?: boolean
  modalBackground?: false | CSS.Properties['background']
  preventClose?: boolean
  style?: React.CSSProperties
  wideModal?: boolean
}

export const ExtranetModal = ({
  showOnProp,
  children,
  closeModal,
  buttons,
  closeOnOutsideClick,
  modalBackground,
  style,
  preventClose,
  footerContent,
  wideModal,
}: ExtranetModalProps) => {
  const [state, setState] = useState<boolean>(
    typeof showOnProp !== 'undefined' ? !!showOnProp : true
  )

  const handleClose = () => {
    if (typeof showOnProp !== 'undefined' && typeof closeModal === 'function') {
      closeModal()
    } else {
      setState(false)
    }
  }
  const { minDesktop } = useBreakpoint()

  const [outsideClickRef] = useClickOutside(() => handleClose())

  useEffect(() => {
    if (typeof showOnProp !== 'undefined' && showOnProp !== state) {
      setState(!!showOnProp)
    }
  }, [showOnProp])

  if (!state) return null

  return (
    <ExtranetModalRoot modalBackground={modalBackground}>
      <ExtranetModalContainer
        ref={!!closeOnOutsideClick && !preventClose ? outsideClickRef : undefined}
        style={style}
        wideModal={wideModal}
      >
        {!!preventClose === false && (
          <ExtranetModalCloseButton onClick={handleClose}>
            <PharmacIcon icon="x" />
          </ExtranetModalCloseButton>
        )}
        <ExtranetModalContainerInner>{children}</ExtranetModalContainerInner>
        {typeof buttons !== 'undefined' && buttons.length > 0 && (
          <ExtranetModalFooter>
            <ExtranetContainer
              full
              flexColumn={!minDesktop}
              marginSmallLeft={minDesktop}
              alignCenter={wideModal}
              spaceBetween={wideModal}
              paddingMediumBottom={wideModal}
            >
              {footerContent}
              <ExtranetContainer
                paddingMediumTop={!minDesktop}
                flexRow={!minDesktop}
                full={!minDesktop || (!footerContent && minDesktop)}
                justifyEnd={minDesktop}
              >
                {buttons.map((button, i) => (
                  <React.Fragment key={'enmb-' + i}>{button}</React.Fragment>
                ))}
              </ExtranetContainer>
            </ExtranetContainer>
          </ExtranetModalFooter>
        )}
      </ExtranetModalContainer>
    </ExtranetModalRoot>
  )
}
